<template>
  <b-modal
    id="page-team-standings-modal"
    :ok-title="trans(block && block.id ? 'save' : 'create')"
    :cancel-title="trans('cancel')"
    size="xl"
    no-enforce-focus
    @shown="onShown"
    @hidden="onHidden"
    @ok="onSave"
  >
    <template #modal-title>
      <span>{{ trans('team_standings') }}</span>
      <small v-if="!!block" class="text-muted ml-2">
        #{{ block.id }}
      </small>
    </template>

    <b-row>
      <b-col class="col-5">
        <b-form-group :label="trans('header')">
          <html-textarea
            :organization="organization"
            v-model="text"
            for-microsite
            show-insert-form-document
          ></html-textarea>
        </b-form-group>

        <b-form-group :label="trans('league')">
          <tags-filter
            :organization="organization"
            type="class"
            multiple
            v-model="tags"
            :all-tags="all_tags"
            @all-tags="all_tags = $event"
            :disabled="busy"
          ></tags-filter>
        </b-form-group>

        <b-form-group :label="trans('division')">
          <tags-filter
            :organization="organization"
            type="class"
            multiple
            v-model="division_tags"
            :any-label="trans('none')"
            :all-tags="all_tags"
            @all-tags="all_tags = $event"
            :disabled="busy"
          ></tags-filter>
        </b-form-group>

        <b-row class="mb-1">
          <b-col>
            {{ trans('dates') }}
            <small class="text-muted" :title="trans('teams_schedule_dates_about')" v-b-tooltip.hover>
              <i class="fa fa-fw fa-info-circle"></i>
            </small>
          </b-col>
          <b-col class="col-auto">
            <b-form-radio-group v-model="date_format" :options="dateFormatOptions" size="sm" class="d-inline-block">
            </b-form-radio-group>
          </b-col>
        </b-row>
        <b-form-group :label="trans('start')" class="mb-1">
          <datetime
            v-model="start"
            size="sm"
            :config="dateOptions"
            :disabled="busy"
          ></datetime>
        </b-form-group>
        <b-form-group :label="trans('end')">
          <datetime
            v-model="end"
            size="sm"
            :config="dateOptions"
            :disabled="busy"
          ></datetime>
        </b-form-group>

        <b-form-group :label="trans('footer')">
          <html-textarea
            :organization="organization"
            v-model="text2"
            for-microsite
            show-insert-form-document
          ></html-textarea>
        </b-form-group>
      </b-col>
      <b-col class="col-7">
        <page-team-standings-block
          v-if="visible"
          :organization="organization"
          :page="page"
          :block="serialized"
          is-preview
        ></page-team-standings-block>
      </b-col>
    </b-row>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>
  </b-modal>
</template>

<script>
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import PageTeamStandingsBlock from './PageTeamStandingsBlock'
  import TagsFilter from './../tag/TagsFilter'
  import Datetime from 'vue-bootstrap-datetimepicker'

  let defaultText = `<h3 style="text-align:center;">${trans('team_standings')}</h3>`

  export default {
    props: {
      organization: Object,
      page: Object,
      block: Object,
      after: Number,
      type: {
        type: String,
        default: 'team-standings',
      },
    },
    components: {
      HtmlTextarea,
      PageTeamStandingsBlock,
      TagsFilter,
      Datetime,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        visible: false,
        text: defaultText,
        text2: null,
        tags: [],
        division_tags: [],
        all_tags: null,
        start: null,
        end: null,
        date_format: 'LL',
      }
    },
    computed: {
      dateFormatOptions() {
        return [
          {value: 'LL', text: trans('date')},
          {value: 'LLL', text: trans('date') + ' & ' + trans('time')},
        ]
      },
      dateOptions() {
        return {
          locale: window.lang,
          format: this.date_format,
          showClear: true,
        }
      },
      serialized() {
        return {
          type: this.type,
          text: this.text,
          text2: this.text2,
          meta: {
            tags: this.tags,
            division_tags: this.division_tags,
            date_format: this.date_format,
            start: !!this.start ? (this.date_format == 'LL' ? moment(this.start, this.date_format).format('YYYY-MM-DD') : moment(this.start, this.date_format).toISOString()) : null,
            end: !!this.end ? (this.date_format == 'LL' ? moment(this.end, this.date_format).format('YYYY-MM-DD') : moment(this.end, this.date_format).toISOString()) : null,
          },
          after: this.after,
        }
      },
    },
    methods: {
      onShown() {
        this.text = !!this.block ? this.block.text : defaultText
        this.text2 = !!this.block ? this.block.text2 : null
        this.tags = !!this.block ? this.block.meta.tags : []
        this.division_tags = !!this.block ? this.block.meta.division_tags || [] : []
        this.date_format = !!this.block && !!this.block.meta && !!this.block.meta.date_format ? this.block.meta.date_format : 'LL'
        this.start = !!this.block && !!this.block.meta.start ?
          moment(this.block.meta.start).format(this.date_format) :
          null
        this.end = !!this.block && !!this.block.meta.end ?
          moment(this.block.meta.end).format(this.date_format) :
          null
        this.$nextTick(() => {
          this.visible = true
        })
      },
      onHidden() {
        this.busy = false
        this.error = null
        this.visible = false
      },
      onSave(e) {
        e.preventDefault()
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/page/${this.page.id}/block`
        if (this.block && this.block.id) url += `/${this.block.id}`
        axios[this.block && this.block.id ? 'patch' : 'post'](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide('page-team-standings-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.pageTeamStandingsModal = this
    },
  }
</script>